import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { PlannedHourSortFields, SortDirection, GetPlannedHoursQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/plannedHour';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const usePlannedHoursTable = ({ queryOptions }: { queryOptions?: GetPlannedHoursQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetPlannedHoursQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as PlannedHourSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withProject: true,
                  withUser: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: plannedHourData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetPlannedHoursQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      plannedHourData
        ? { totalCount: plannedHourData.plannedHours.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [plannedHourData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type PlannedHoursBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function PlannedHoursBaseTable(props: PlannedHoursBaseTableProps) {
  const tableProps = usePlannedHoursTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
